/// <reference path="../../types.d.ts" />
import { state } from "../core/state";
import { t } from "../core/i18n";
import {
  escapeHtml,
  formatLifeYears,
  extractNumericYear,
  renderAvatarSilhouette,
} from "../core/helpers";
import { getParents, getSpousesAndChildren } from "../engine/graph";

export interface FanContext {
  focusPersonInTree: (pid: string) => void;
  openPersonDrawer: (pid: string) => void;
}

let fanCtx: FanContext = {
  focusPersonInTree: () => {},
  openPersonDrawer: () => {},
};

export function setupFanChart(context: FanContext): void {
  fanCtx = context;
}

export const FAN_BRANCH_HUES = [152, 28, 212, 336, 186, 268, 8, 96, 48, 300, 232, 120];
export const FAN_GENERATION_HUES = [152, 186, 212, 262, 300, 335, 24, 45];
export const FAN_START_ANGLE = -160;
export const FAN_END_ANGLE = 160;

export function fanRound(n: number): number {
  return Math.round(n * 100) / 100;
}

export function fanPolar(r: number, angleDeg: number): { x: number; y: number } {
  const rad = ((angleDeg - 90) * Math.PI) / 180.0;
  return { x: fanRound(r * Math.cos(rad)), y: fanRound(r * Math.sin(rad)) };
}

export function fanSectorPath(rInner: number, rOuter: number, startAngle: number, endAngle: number): string {
  const span = Math.min(359.9, endAngle - startAngle);
  const endA = startAngle + span;
  const p1 = fanPolar(rOuter, endA);
  const p2 = fanPolar(rOuter, startAngle);
  const p3 = fanPolar(rInner, startAngle);
  const p4 = fanPolar(rInner, endA);
  const largeArc = span > 180 ? 1 : 0;
  return `M ${p1.x} ${p1.y} A ${rOuter} ${rOuter} 0 ${largeArc} 0 ${p2.x} ${p2.y} L ${p3.x} ${p3.y} A ${rInner} ${rInner} 0 ${largeArc} 1 ${p4.x} ${p4.y} Z`;
}

// Arc used as a <textPath>. On the lower half the arc is reversed so text never renders upside down.
export function fanLabelArc(r: number, a0: number, a1: number, upright: boolean): string {
  const p0 = fanPolar(r, a0);
  const p1 = fanPolar(r, a1);
  const large = a1 - a0 > 180 ? 1 : 0;
  return upright
    ? `M ${p0.x} ${p0.y} A ${r} ${r} 0 ${large} 1 ${p1.x} ${p1.y}`
    : `M ${p1.x} ${p1.y} A ${r} ${r} 0 ${large} 0 ${p0.x} ${p0.y}`;
}

export function fanNormalizeAngle(angle: number): number {
  return ((((angle + 180) % 360) + 360) % 360) - 180;
}

export function fanCompactYears(p: PersonRecord): string {
  const bYr = extractNumericYear(p.birthYear);
  const dYr = extractNumericYear(p.deathYear);
  const b = bYr || (p.birthYear ? String(p.birthYear).slice(0, 10) : "");
  const d = dYr || (p.deathYear ? String(p.deathYear).slice(0, 10) : "");
  if (b && d) return `${b}–${d}`;
  if (b && p.deceased) return `${b}–?`;
  if (b) return `${b}`;
  if (d) return `?–${d}`;
  return "";
}

export function fanFitName(p: PersonRecord, maxPx: number, fontSize: number): string {
  const maxChars = Math.floor(maxPx / (fontSize * 0.62));
  if (maxChars < 2) return "";
  const given = String(p.given || "").trim();
  const sur = String(p.surname || "").trim();
  const full = String(p.name || `${given} ${sur}`).trim();
  const first = (given || full).split(/\s+/)[0] || "";
  const candidates = [full];
  if (given && sur) candidates.push(`${given} ${sur[0]}.`);
  if (given) candidates.push(given);
  if (first) candidates.push(first);
  for (const c of candidates) {
    if (c && c.length <= maxChars) return c;
  }
  const base = first || full;
  return maxChars >= 3 ? `${base.slice(0, maxChars - 1)}…` : base.slice(0, maxChars);
}

export function fanAvatarMarkup(x: number, y: number, size: number, p: PersonRecord, withBackdrop = true): string {
  const s = fanRound(size);
  const bg = withBackdrop ? `<circle class="fan-avatar-bg" cx="${fanRound(x)}" cy="${fanRound(y)}" r="${fanRound(size * 0.64)}" />` : "";
  return (
    bg +
    renderAvatarSilhouette(p.sex, Boolean(p.deceased)).replace(
      'width="100%" height="100%"',
      `x="${fanRound(x - size / 2)}" y="${fanRound(y - size / 2)}" width="${s}" height="${s}" style="width:${s}px;height:${s}px"`
    )
  );
}

export function fanRelationLabel(ring: number, sex: string, mode: string, side: string | null): string {
  const tr = state.lang === "tr";
  if (ring === 0) return tr ? "Odak kişi" : "Focus person";
  const male = sex === "M";
  const female = sex === "F";
  if (mode === "ancestors") {
    let base: string;
    if (tr) {
      if (ring === 1) base = male ? "Babası" : female ? "Annesi" : "Ebeveyni";
      else if (ring === 2) base = male ? "Dedesi" : female ? "Ninesi" : "Büyük ebeveyni";
      else if (ring === 3) base = male ? "Büyük dedesi" : female ? "Büyük ninesi" : "Büyük büyük ebeveyni";
      else base = `${ring}. kuşak atası`;
    } else {
      const noun = male ? "father" : female ? "mother" : "parent";
      if (ring === 1) base = male ? "Father" : female ? "Mother" : "Parent";
      else if (ring === 2) base = `Grand${noun}`;
      else if (ring === 3) base = `Great-grand${noun}`;
      else base = `${ring - 2}× great-grand${noun}`;
    }
    if (ring >= 2 && side) {
      base += tr ? (side === "paternal" ? " · baba tarafı" : " · anne tarafı") : side === "paternal" ? " · paternal" : " · maternal";
    }
    return base;
  }
  if (tr) {
    if (ring === 1) return male ? "Oğlu" : female ? "Kızı" : "Çocuğu";
    if (ring === 2) return "Torunu";
    if (ring === 3) return "Torununun çocuğu";
    return `${ring}. kuşak torunu`;
  }
  if (ring === 1) return male ? "Son" : female ? "Daughter" : "Child";
  if (ring === 2) return "Grandchild";
  if (ring === 3) return "Great-grandchild";
  return `${ring - 2}× great-grandchild`;
}

export function fanRingTitle(ring: number, mode: string): string {
  const tr = state.lang === "tr";
  const labels =
    mode === "ancestors"
      ? tr
        ? ["Ebeveynler", "Dede & Nineler", "Büyük Dedeler"]
        : ["Parents", "Grandparents", "Great-grandparents"]
      : tr
        ? ["Çocuklar", "Torunlar", "Torun Çocukları"]
        : ["Children", "Grandchildren", "Great-grandchildren"];
  return labels[ring - 1] || (tr ? `${ring}. Kuşak` : `Generation ${ring}`);
}

export function renderFanChart(): void {
  if (!state.data) return;
  const rootPerson = state.data.individuals[state.focusedId];
  if (!rootPerson) return;

  const tr = state.lang === "tr";
  const focusedNameEl = document.getElementById("fan-focused-name");
  if (focusedNameEl) focusedNameEl.textContent = rootPerson.name;
  const mode = (document.getElementById("fan-direction-select") as HTMLSelectElement)?.value || "ancestors";
  const maxRings = parseInt((document.getElementById("fan-rings-select") as HTMLSelectElement)?.value, 10) || 5;
  const colorSel = document.getElementById("fan-color-select") as HTMLSelectElement | null;
  const layoutSel = document.getElementById("fan-layout-select") as HTMLSelectElement | null;
  const colorMode = colorSel ? colorSel.value : "branch";
  const balanced = !layoutSel || layoutSel.value !== "equal";
  const svg = document.getElementById("fan-svg");
  if (!svg) return;

  // Adaptive ring sizing to fit up to 8 generations without clipping
  let ringRadius = 68;
  let centerR = 62;
  if (maxRings === 6) {
    ringRadius = 62;
    centerR = 58;
  } else if (maxRings === 7) {
    ringRadius = 52;
    centerR = 52;
  } else if (maxRings >= 8) {
    ringRadius = 45;
    centerR = 48;
  }

  // Update navigation buttons in toolbar
  const resetRootBtn = document.getElementById("fan-reset-root-btn");
  if (resetRootBtn) {
    resetRootBtn.classList.toggle("hidden", state.focusedId === "I227");
  }
  const backBtn = document.getElementById("fan-back-btn");
  if (backBtn) {
    backBtn.classList.toggle("hidden", !state.focusHistory || state.focusHistory.length === 0);
  }

  const rootParents = mode === "ancestors" ? getParents(rootPerson.id) : null;
  const getNext = (person: PersonRecord): PersonRecord[] => {
    if (mode === "ancestors") {
      const { father, mother } = getParents(person.id);
      return [father, mother].filter((p): p is PersonRecord => Boolean(p));
    }
    const out: PersonRecord[] = [];
    for (const u of getSpousesAndChildren(person.id)) {
      for (const ch of u.children) out.push(ch);
    }
    return out;
  };

  // 1. Build the visible tree (with branch / side metadata and leaf weights)
  function buildNode(person: PersonRecord, ring: number, key: string, side: string | null, branchIdx: number): any {
    const node: any = { person, ring, key, side, branchIdx, children: [], leaves: 1, more: 0 };
    const next = getNext(person);
    if (ring < maxRings) {
      next.forEach((rel, i) => {
        let childSide = side;
        let childBranch = branchIdx;
        if (ring === 0) {
          childBranch = i;
          if (mode === "ancestors") {
            childSide = rootParents?.father && rel.id === rootParents.father.id ? "paternal" : "maternal";
          }
        }
        node.children.push(buildNode(rel, ring + 1, `${key}.${i}`, childSide, childBranch));
      });
      if (node.children.length) node.leaves = node.children.reduce((s: number, c: any) => s + c.leaves, 0);
    } else {
      node.more = next.length;
    }
    return node;
  }

  // 2. Angular layout: "balanced" splits by visible leaf count, "equal" splits evenly
  function layoutNode(node: any, a0: number, a1: number): void {
    node.a0 = a0;
    node.a1 = a1;
    if (!node.children.length) return;
    const weights = node.children.map((c: any) => (balanced ? c.leaves : 1));
    const total = weights.reduce((s: number, w: number) => s + w, 0);
    let cur = a0;
    node.children.forEach((c: any, i: number) => {
      const span = ((a1 - a0) * weights[i]) / total;
      layoutNode(c, cur, cur + span);
      cur += span;
    });
  }

  const root = buildNode(rootPerson, 0, "0", null, -1);
  layoutNode(root, FAN_START_ANGLE, FAN_END_ANGLE);

  function colorVars(node: any): { h: number; s: number } {
    const p = node.person;
    const dead = Boolean(p.deceased);
    if (colorMode === "gender") return { h: p.sex === "M" ? 214 : p.sex === "F" ? 334 : 38, s: dead ? 28 : 64 };
    if (colorMode === "generation") return { h: FAN_GENERATION_HUES[(node.ring - 1) % FAN_GENERATION_HUES.length], s: dead ? 26 : 58 };
    if (colorMode === "status") return { h: dead ? 36 : 152, s: dead ? 7 : 52 };
    return { h: FAN_BRANCH_HUES[Math.max(0, node.branchIdx) % FAN_BRANCH_HUES.length], s: dead ? 26 : 60 };
  }

  const defs: string[] = [];
  const sectors: string[] = [];
  const nodeIndex: Record<string, any> = { "0": root };
  const seen = new Set<string>();
  let livingCount = 0;
  let deceasedCount = 0;
  let depth = 0;
  let arcId = 0;

  function drawSector(node: any): void {
    const p = node.person;
    const dead = Boolean(p.deceased);
    const rIn = centerR + (node.ring - 1) * ringRadius;
    const rOut = centerR + node.ring * ringRadius - 2;
    const span = node.a1 - node.a0;
    const mid = (node.a0 + node.a1) / 2;
    const midR = (rIn + rOut) / 2;
    const arcLen = (midR * span * Math.PI) / 180;
    const { h, s } = colorVars(node);
    const g: string[] = [];

    g.push(`<path class="fan-shape" d="${fanSectorPath(rIn, rOut, node.a0, node.a1)}" />`);
    if (span > 1.2) {
      g.push(`<path class="fan-accent" d="${fanSectorPath(rIn, rIn + 3.2, node.a0 + 0.25, node.a1 - 0.25)}" />`);
    }
    if (node.more > 0) {
      const inset = Math.min(0.6, span * 0.15);
      g.push(`<path class="fan-more" d="${fanSectorPath(rOut + 3, rOut + 7, node.a0 + inset, node.a1 - inset)}" />`);
    }

    const top = Math.abs(fanNormalizeAngle(mid)) <= 90;
    const u = top ? 1 : -1;
    const pad = Math.min(2.2, span * 0.08);

    if (arcLen >= 58) {
      // Wide sector: curved name + years following the arc, avatar badge on top
      const av = fanPolar(midR + u * 18, mid);
      g.push(fanAvatarMarkup(av.x, av.y, Math.min(15, ringRadius * 0.28), p));
      const nameR = midR + u * 1.5;
      const nameLen = (nameR * (span - 2 * pad) * Math.PI) / 180 - 6;
      const name = fanFitName(p, nameLen, 10);
      const idN = `fan-arc-${arcId++}`;
      defs.push(`<path id="${idN}" d="${fanLabelArc(nameR, node.a0 + pad, node.a1 - pad, top)}" />`);
      g.push(
        `<text class="fan-name" font-size="10" dominant-baseline="middle"><textPath href="#${idN}" startOffset="50%" text-anchor="middle">${escapeHtml(name)}</textPath></text>`
      );
      const yrs = fanCompactYears(p);
      if (yrs) {
        const yrsR = midR - u * 11.5;
        const idY = `fan-arc-${arcId++}`;
        defs.push(`<path id="${idY}" d="${fanLabelArc(yrsR, node.a0 + pad, node.a1 - pad, top)}" />`);
        g.push(
          `<text class="fan-years" font-size="8" dominant-baseline="middle"><textPath href="#${idY}" startOffset="50%" text-anchor="middle">${escapeHtml(yrs)}</textPath></text>`
        );
      }
    } else if (arcLen >= 10) {
      // Narrow sector: radial text, always rotated to stay readable
      const m = fanNormalizeAngle(mid);
      const rot = m >= 0 ? m - 90 : m + 90;
      const fs = arcLen >= 14 ? 8.5 : 7.2;
      let textR = midR;
      let avail = rOut - rIn - 8;
      if (arcLen >= 18) {
        const avSize = Math.min(13, arcLen - 4);
        const av = fanPolar(rIn + 5 + avSize / 2, mid);
        g.push(fanAvatarMarkup(av.x, av.y, avSize, p));
        textR = (rIn + avSize + 8 + rOut - 3) / 2;
        avail = rOut - 3 - (rIn + avSize + 8);
      }
      const c = fanPolar(textR, mid);
      const name = fanFitName(p, avail, fs);
      const yrs = arcLen >= 22 ? fanCompactYears(p) : "";
      g.push(
        `<g transform="translate(${c.x} ${c.y}) rotate(${fanRound(rot)})">` +
          `<text class="fan-name" font-size="${fs}" text-anchor="middle" dominant-baseline="middle" y="${yrs ? -4.2 : 0}">${escapeHtml(name)}</text>` +
          (yrs ? `<text class="fan-years" font-size="7.2" text-anchor="middle" dominant-baseline="middle" y="5.4">${escapeHtml(yrs)}</text>` : "") +
          `</g>`
      );
    } else {
      // Micro sector: subtle centered dot
      const c = fanPolar(midR, mid);
      g.push(`<circle cx="${c.x}" cy="${c.y}" r="2" fill="currentColor" opacity="0.45" pointer-events="none" />`);
    }

    sectors.push(
      `<g class="fan-sector${dead ? " is-deceased" : ""}" data-person-id="${p.id}" data-key="${node.key}" style="--h:${h};--s:${s}%;--ring:${node.ring};--delay:${node.ring * 70}ms" role="button" aria-label="${escapeHtml(p.name)}">${g.join("")}</g>`
    );
  }

  function walk(node: any): void {
    if (node.ring > 0) {
      nodeIndex[node.key] = node;
      drawSector(node);
      depth = Math.max(depth, node.ring);
      if (!seen.has(node.person.id)) {
        seen.add(node.person.id);
        if (node.person.deceased) deceasedCount++;
        else livingCount++;
      }
    }
    node.children.forEach(walk);
  }
  walk(root);

  const outerR = centerR + maxRings * ringRadius;
  const halfBox = Math.ceil(outerR + 24);
  svg.setAttribute("viewBox", `${-halfBox} ${-halfBox} ${halfBox * 2} ${halfBox * 2}`);
  const guides: string[] = [];
  for (let i = 1; i <= maxRings; i++) {
    guides.push(`<circle class="fan-guide" cx="0" cy="0" r="${centerR + i * ringRadius - 1}" />`);
  }
  const ringTitles: string[] = [];
  for (let i = 1; i <= depth; i++) {
    const midR = centerR + (i - 0.5) * ringRadius - 1;
    const id = `fan-ring-lbl-${i}`;
    defs.push(`<path id="${id}" d="${fanLabelArc(midR, 163, 197, false)}" />`);
    ringTitles.push(
      `<text class="fan-ring-label" dominant-baseline="middle"><textPath href="#${id}" startOffset="50%" text-anchor="middle">${escapeHtml(fanRingTitle(i, mode))}</textPath></text>`
    );
  }

  // Center (focus person)
  const clip = (str: string, n: number) => (str.length > n ? `${str.slice(0, n - 1)}…` : str);
  const centerGiven = clip(String(rootPerson.given || rootPerson.name || ""), 14);
  const centerSur = clip(String(rootPerson.surname || ""), 16);
  const centerYears = fanCompactYears(rootPerson);
  const center = `
    <g class="fan-sector fan-center${rootPerson.deceased ? " is-deceased" : ""}" data-person-id="${rootPerson.id}" data-key="0" role="button" aria-label="${escapeHtml(rootPerson.name)}">
      <circle class="fan-center-disc" cx="0" cy="0" r="${centerR - 4}" />
      <circle class="fan-center-halo" cx="0" cy="0" r="${centerR - 3}" />
      <circle class="fan-center-inner" cx="0" cy="0" r="${centerR - 9}" />
      ${fanAvatarMarkup(0, -29, 19, rootPerson)}
      <text class="fan-center-name" x="0" y="-3" text-anchor="middle" dominant-baseline="middle">${escapeHtml(centerGiven)}</text>
      <text class="fan-center-surname" x="0" y="11" text-anchor="middle" dominant-baseline="middle">${escapeHtml(centerSur)}</text>
      ${centerYears ? `<text class="fan-center-years" x="0" y="25" text-anchor="middle" dominant-baseline="middle">${escapeHtml(centerYears)}</text>` : ""}
    </g>`;

  const empty =
    depth === 0
      ? `<text class="fan-empty" x="0" y="${centerR + 34}" text-anchor="middle">${
          tr ? "Bu yönde kayıtlı akraba bulunamadı" : "No relatives recorded in this direction"
        }</text>`
      : "";

  svg.innerHTML = `
    <defs>
      <radialGradient id="fan-center-grad" cx="50%" cy="32%" r="78%">
        <stop offset="0%" class="fan-cg-a" />
        <stop offset="100%" class="fan-cg-b" />
      </radialGradient>
      <filter id="fan-soft-shadow" x="-10%" y="-10%" width="120%" height="120%">
        <feDropShadow dx="0" dy="4" stdDeviation="8" flood-opacity="0.12" />
      </filter>
      ${defs.join("")}
    </defs>
    <circle class="fan-backdrop" cx="0" cy="0" r="${outerR + 8}" filter="url(#fan-soft-shadow)" />
    ${guides.join("")}
    ${ringTitles.join("")}
    ${sectors.join("")}
    ${center}
    ${empty}
    <path id="fan-hover-outline" class="fan-hover-outline" d="" />
  `;

  state.fanNodeIndex = nodeIndex;
  state.fanMode = mode;

  renderFanLegend({ root, colorMode, mode, depth, livingCount, deceasedCount, relatives: seen.size });

  if (!svg.dataset.fanBound) {
    svg.dataset.fanBound = "1";
    bindFanInteractions(svg);
  }
}

export function renderFanLegend({ root, colorMode, mode, depth, livingCount, deceasedCount, relatives }: any): void {
  const el = document.getElementById("fan-legend");
  if (!el) return;
  const tr = state.lang === "tr";
  const sw = (h: number, s: number) => `<i class="fan-swatch" style="background:hsl(${h}, ${s}%, 56%)"></i>`;

  const stats = `
    <div class="fan-legend-stats">
      <span><b>${relatives}</b> ${tr ? "akraba" : "relatives"}</span>
      <span><i class="dot dot-living"></i><b>${livingCount}</b> ${tr ? "hayatta" : "living"}</span>
      <span><i class="dot dot-deceased"></i><b>${deceasedCount}</b> ${tr ? "vefat" : "deceased"}</span>
      <span><b>${depth}</b> ${tr ? "kuşak" : depth === 1 ? "generation" : "generations"}</span>
    </div>`;

  let items: string[] = [];
  if (colorMode === "gender") {
    items = [
      `<span class="fan-legend-item">${sw(214, 64)}${tr ? "Erkek" : "Male"}</span>`,
      `<span class="fan-legend-item">${sw(334, 64)}${tr ? "Kadın" : "Female"}</span>`,
      `<span class="fan-legend-item">${sw(38, 64)}${tr ? "Belirsiz" : "Unknown"}</span>`,
    ];
  } else if (colorMode === "generation") {
    for (let i = 1; i <= depth; i++) {
      items.push(`<span class="fan-legend-item">${sw(FAN_GENERATION_HUES[(i - 1) % FAN_GENERATION_HUES.length], 58)}${escapeHtml(fanRingTitle(i, mode))}</span>`);
    }
  } else if (colorMode === "status") {
    items = [
      `<span class="fan-legend-item">${sw(152, 52)}${tr ? "Hayatta" : "Living"}</span>`,
      `<span class="fan-legend-item">${sw(36, 7)}${tr ? "Vefat etmiş" : "Deceased"}</span>`,
    ];
  } else {
    const firstRing = root.children;
    const shown = firstRing.slice(0, 8);
    items = shown.map((n: any) => {
      const h = FAN_BRANCH_HUES[Math.max(0, n.branchIdx) % FAN_BRANCH_HUES.length];
      const label =
        mode === "ancestors"
          ? `${n.side === "paternal" ? (tr ? "Baba tarafı" : "Paternal") : tr ? "Anne tarafı" : "Maternal"} · ${n.person.given || n.person.name}`
          : n.person.given || n.person.name;
      return `<span class="fan-legend-item" title="${escapeHtml(n.person.name)}">${sw(h, 60)}${escapeHtml(label)}</span>`;
    });
    if (firstRing.length > shown.length) items.push(`<span class="fan-legend-item">+${firstRing.length - shown.length}</span>`);
  }

  const note =
    colorMode === "status"
      ? ""
      : `<div class="fan-legend-note">${
          tr
            ? "Canlı ton ve dolu figür: hayatta · Soluk ton ve boş figür: vefat · Dış çizgi: daha fazla kuşak var"
            : "Vivid tone & solid figure: living · Faded tone & outline figure: deceased · Outer tick: more generations"
        }</div>`;

  el.innerHTML = `${stats}${items.length ? `<div class="fan-legend-colors">${items.join("")}</div>` : ""}${note}`;
  el.classList.toggle("hidden", depth === 0);
}

export function fanTooltipHtml(node: any): string {
  const p = node.person;
  const tr = state.lang === "tr";
  const dead = Boolean(p.deceased);
  const unions = getSpousesAndChildren(p.id);
  const kids = unions.reduce((s, u) => s + u.children.length, 0);
  const spouses = unions.filter((u) => u.spouse).map((u) => u.spouse!.name);
  const row = (label: string, value: string | number) => `<div class="fan-tip-row"><span>${label}</span><b>${value}</b></div>`;
  return `
    <div class="fan-tip-head">
      <span class="fan-tip-avatar">${renderAvatarSilhouette(p.sex, dead)}</span>
      <div class="fan-tip-titles">
        <strong>${escapeHtml(p.name)}</strong>
        <span class="fan-tip-rel">${escapeHtml(fanRelationLabel(node.ring, p.sex, state.fanMode, node.side))}</span>
      </div>
    </div>
    ${row(tr ? "Yaşam" : "Life", escapeHtml(formatLifeYears(p)))}
    ${row(tr ? "Durum" : "Status", `<span class="fan-tip-status ${dead ? "is-dead" : "is-alive"}">${dead ? t("deceased") : t("living")}</span>`)}
    ${p.birthPlace ? row(tr ? "Doğum yeri" : "Birthplace", escapeHtml(p.birthPlace)) : ""}
    ${spouses.length ? row(tr ? "Eş" : "Spouse", escapeHtml(spouses.join(", "))) : ""}
    ${kids ? row(tr ? "Çocuk" : "Children", kids) : ""}
    ${
      node.more
        ? `<div class="fan-tip-more">+${node.more} ${tr ? "kişi daha (halka sayısını artırın)" : "more (increase rings)"}</div>`
        : ""
    }
    <div class="fan-tip-hint">${
      node.ring === 0 ? (tr ? "Detaylar için tıklayın" : "Click for details") : tr ? "Merkeze almak için tıklayın" : "Click to center"
    }</div>`;
}

export function bindFanInteractions(svg: HTMLElement): void {
  const wrap = document.getElementById("fan-canvas-wrap");
  const tip = document.getElementById("fan-tooltip");
  let currentKey: string | null = null;

  const clear = () => {
    currentKey = null;
    svg.classList.remove("is-tracing");
    svg.querySelectorAll(".fan-sector.is-trace, .fan-sector.is-hover").forEach((el) => el.classList.remove("is-trace", "is-hover"));
    const ol = svg.querySelector("#fan-hover-outline");
    if (ol) ol.setAttribute("d", "");
    if (tip) tip.classList.add("hidden");
  };

  const positionTip = (e: PointerEvent) => {
    if (!tip || !wrap) return;
    const rect = wrap.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    let x = e.clientX - rect.left + 16;
    let y = e.clientY - rect.top + 16;
    if (x + tw > rect.width - 8) x = e.clientX - rect.left - tw - 16;
    if (y + th > rect.height - 8) y = e.clientY - rect.top - th - 16;
    tip.style.transform = `translate(${Math.max(8, x)}px, ${Math.max(8, y)}px)`;
  };

  svg.addEventListener("pointerover", (e: any) => {
    const sec = e.target.closest(".fan-sector");
    if (!sec) return;
    const key = sec.dataset.key || "";
    if (key === currentKey) return;
    currentKey = key;

    svg.classList.add("is-tracing");
    svg.querySelectorAll(".fan-sector").forEach((el: any) => {
      const k = el.dataset.key || "";
      el.classList.toggle("is-trace", k === key || key.startsWith(`${k}.`) || k.startsWith(`${key}.`));
      el.classList.toggle("is-hover", k === key);
    });
    const shape = sec.querySelector(".fan-shape");
    const ol = svg.querySelector("#fan-hover-outline");
    if (ol) ol.setAttribute("d", shape ? shape.getAttribute("d") : "");

    const node = state.fanNodeIndex && state.fanNodeIndex[key];
    if (tip && node && e.pointerType !== "touch") {
      tip.innerHTML = fanTooltipHtml(node);
      tip.classList.remove("hidden");
      positionTip(e);
    }
  });

  svg.addEventListener("pointermove", (e: any) => {
    if (tip && !tip.classList.contains("hidden")) positionTip(e);
  });

  svg.addEventListener("pointerout", (e: any) => {
    const to = e.relatedTarget;
    if (!to || !svg.contains(to) || !to.closest(".fan-sector")) clear();
  });

  svg.addEventListener("click", (e: any) => {
    const sec = e.target.closest(".fan-sector");
    if (!sec) return;
    const pid = sec.getAttribute("data-person-id");
    clear();
    if (pid === state.focusedId) fanCtx.openPersonDrawer(pid);
    else fanCtx.focusPersonInTree(pid);
  });
}
